import { useEffect, useState } from 'react'

import { AgoraRTCClientWrapper } from '@core/agoraRTC/libs/AgoraRTCClientWrapper'
import { loadAgoraRTC } from '@core/agoraRTC/libs/utils'
import { isDebug } from '@core/debug'

/** Lazily instantiates an Agora client wrapper when it is needed. */
export function useLazyAgoraClient(
  shouldLoad: boolean,
  enableInstantVideo: boolean,
  enableLogUpload: boolean = false,
) {
  const [client, setClient] = useState<AgoraRTCClientWrapper | undefined>(
    undefined,
  )

  useEffect(() => {
    const loadAgoraSdk = async () => {
      const AgoraRTC = await loadAgoraRTC()
      /**
       * - 0: DEBUG. Output all API logs.
       * - 1: INFO. Output logs of the INFO, WARNING and ERROR level.
       * - 2: WARNING. Output logs of the WARNING and ERROR level.
       * - 3: ERROR. Output logs of the ERROR level.
       * - 4: NONE. Do not output any log.
       */
      AgoraRTC.setLogLevel(isDebug() ? 0 : 3)
      if (enableLogUpload) {
        AgoraRTC.enableLogUpload()
      }
      if (enableInstantVideo) {
        // `setParameter` & 'ENABLED_INSTANT_VIDEO' are undocumented features. It
        // is provided to Firework by Agora for a new Agora feature.
        ;(AgoraRTC as unknown as any).setParameter?.(
          'ENABLE_INSTANT_VIDEO',
          true,
        )
      }
      const rtcClient = AgoraRTC.createClient({
        mode: 'live',
        codec: 'vp8',
      })
      const rtcClientWrapper = new AgoraRTCClientWrapper(rtcClient)
      setClient(rtcClientWrapper)
    }

    if (shouldLoad) {
      if (!client) {
        loadAgoraSdk()
      }
    } else {
      client?.reset()
      setClient(undefined)
    }
  }, [client, shouldLoad, enableInstantVideo, enableLogUpload])

  return client
}
